<template>
  <div class="mandala-goal-list p-4">
    <RecordListPrintHeader :title="selectedYearMonthJpStr + ' 月間目標一覧'">
      <div class="mandala-goal-list__right">
        <div class="mandala-goal-list__buttons">
          <MiniButton
            class="mb-3"
            @click="$router.push({ name: 'recordListMandalaGoalSelect' })">
            検索条件に戻る
          </MiniButton>
        </div>
        <ChartLegends :legends="legends" />
      </div>
    </RecordListPrintHeader>
    <div class="mandala-goal-list__items row">
      <MandalaEvaluationPreviewItem
        v-for="member in members"
        :key="member.id"
        :data-member-id="member.id"
        :user="member"
        :selected="member.id === highlightMemberId"
        :mandala-evaluations="mandalaEvaluationsByUserId[member.id] || []"
        :mandala-goal-template="getMandalaGoalTemplate(member.id)"
        :year-months="latest3YearMonths"
        :like-ids="likeIds"
        back-to="recordMandalaList"
        @click="showMandalaEvaluationDialog(member.id)"
        @like="onLike" />
    </div>
    <div class="mandala-goal-list__buttons">
      <MiniButton
        @click="$router.push({ name: 'recordListMandalaGoalSelect' })">
        検索条件に戻る
      </MiniButton>
    </div>
    <PreviewMandalaEvaluationModal
      v-if="selectedMember"
      v-model="isShowMandalaEvaluationDialog"
      :is-any-manager="true"
      :user="selectedMember"
      :year-months="latest3YearMonths"
      :like-ids="likeIds"
      :mandala-evaluations="mandalaEvaluationsByUserId[selectedMember.id] || []"
      :mandala-goal-template="getMandalaGoalTemplate(selectedMember.id)"
      @like="onLike" />
  </div>
</template>

<script>
import { addMonths, format, startOfMonth } from 'date-fns'
import MandalaEvaluationPreviewItem from '@/components/view/records/MandalaEvaluationPreviewItem.vue'
import MiniButton from '@/components/parts/MiniButton.vue'
import ChartLegends from '@/components/charts/ChartLegends.vue'
import RecordListPrintHeader from '@/components/view/records/RecordListPrintHeader.vue'
import { buildMemberFilterApiParams, buildOrderingByFilterQuery } from '@/helper/records'
import PreviewMandalaEvaluationModal from '@/components/view/PreviewMandalaEvaluationModal.vue'
import mandalaEvaluationLike from '@/mixins/mandalaEvaluationLike'

const CHART_COLORS = ['#E91E63', '#F48FB1', '#BDBDBD']

export default {
  components: {
    MandalaEvaluationPreviewItem,
    MiniButton,
    ChartLegends,
    RecordListPrintHeader,
    PreviewMandalaEvaluationModal,
  },
  mixins: [mandalaEvaluationLike],
  data() {
    return {
      members: [],
      mandalaEvaluations: [],
      likeIds: [],
      mandalaGoalTemplates: [],
      highlightMemberId: null,
      selectedMemberId: null,
      isShowMandalaEvaluationDialog: false,
    }
  },
  computed: {
    selectedYearMonthDate() {
      const yearMonth = this.$route.query.yearMonth
      return startOfMonth(yearMonth ? new Date(yearMonth) : new Date())
    },
    selectedYearMonthJpStr() {
      return format(this.selectedYearMonthDate, 'yyyy年M月')
    },
    latest3YearMonthDates() {
      return [
        this.selectedYearMonthDate,
        startOfMonth(addMonths(this.selectedYearMonthDate, -1)),
        startOfMonth(addMonths(this.selectedYearMonthDate, -2)),
      ]
    },
    latest3YearMonthLabels() {
      return this.latest3YearMonthDates.map(date => format(date, 'yyyy年M月'))
    },
    latest3YearMonths() {
      return this.latest3YearMonthDates.map(date => format(date, 'yyyy-MM-dd'))
    },
    legends() {
      return this.latest3YearMonthLabels.map((label, index) => ({ label, color: CHART_COLORS[index] }))
    },
    mandalaEvaluationsByUserId() {
      return this.mandalaEvaluations.reduce((prev, curr) => {
        if (curr.user.id in prev) {
          return { ...prev, [curr.user.id]: [...prev[curr.user.id], curr] }
        }
        return { ...prev, [curr.user.id]: [curr] }
      }, {})
    },
    mandalaGoalTemplateById() {
      return this.mandalaGoalTemplates.reduce((prev, curr) => {
        return { ...prev, [curr.id]: curr }
      }, {})
    },
    selectedMember() {
      return this.members.find(member => member.id === this.selectedMemberId) || null
    },
  },
  async created() {
    this.$store.commit('startLoading')
    try {
      await Promise.all([
        this.fetchMembers(),
        this.fetchMandalaEvaluations(),
        this.fetchLikeIds(),
        this.fetchMandalaGoalTemplates(),
      ])
    } finally {
      this.$store.commit('finishLoading')
    }
    this.$nextTick(() => {
      const memberId = this.$store.state.recordProfile.returnMemberId
      if (memberId) {
        this.$store.commit('recordProfile/clearReturnMemberId')
        this.highlightMemberId = memberId
        const el = document.querySelector(`[data-member-id="${memberId}"]`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    })
  },
  methods: {
    buildFilterParams() {
      // mandala_goal はルート遷移時に必ずクエリパラメータを渡す設計のため
      // filterParams へのフォールバックは不要
      return buildMemberFilterApiParams(this.$route.query)
    },
    fetchMembers() {
      const params = { ordering: buildOrderingByFilterQuery(this.$route.query), limit: 999999, ...this.buildFilterParams() }
      return this.$axios.get('/users/', { params }).then(res => {
        this.members = res.data.results
      })
    },
    buildApiParams() {
      return {
        year_month__in: this.latest3YearMonths.join(','),
        limit: 999999,
        ...this.buildFilterParams(),
      }
    },
    fetchMandalaEvaluations() {
      return this.$axios.get('/mandala_goal_self_evaluations/', {
        params: this.buildApiParams(),
      }).then(res => {
        this.mandalaEvaluations = res.data.results
      })
    },
    fetchLikeIds() {
      return this.$axios.get('/mandala_goal_self_evaluations/like_ids/', {
        params: { yearMonth: this.latest3YearMonths[0], limit: 999999 },
      }).then(res => {
        this.likeIds = res.data.results
      })
    },
    fetchMandalaGoalTemplates() {
      return this.$axios.get('/mandala_goal_templates/', {
        params: { orderBy: 'display_order' },
      }).then(res => {
        this.mandalaGoalTemplates = res.data.results
      })
    },
    getMandalaGoalTemplate(userId) {
      const evaluation = this.mandalaEvaluationsByUserId[userId]?.[0]
      if (!evaluation) return null
      return this.mandalaGoalTemplateById[evaluation.mandalaGoalTemplate]
    },
    showMandalaEvaluationDialog(memberId) {
      this.isShowMandalaEvaluationDialog = true
      this.selectedMemberId = memberId
    },
  },
}
</script>

<style lang="scss">
@page {
  size: A4 portrait;
}

@media print {
  .mandala-goal-list__items .mandala-evaluation-preview-item {
    width: 33.333% !important;
    flex: 0 0 33.333% !important;
    max-width: 33.333% !important;
  }
}
</style>

<style lang="scss" scoped>
.mandala-goal-list {
  background-color: white;
  min-height: 100vh;

  &__right {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;

    @media (max-width: 767px) {
      align-items: center;
    }
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    @media (max-width: 767px) {
      align-items: center;
    }
  }
}
</style>
